import * as React from 'react';
import classes from '../Auth.module.scss';
import { Input } from '../../../components/forms/Input';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { Checkbox } from '../../../components/forms/Checkbox';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { RegisterWithEmailFormVariables } from './SignupStep2.controller';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { Void } from '../../../models/basic-objects';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { SocialAuthTokenDataType } from '../Signup/Signup.apollo';
import { FacebookSignupLabel } from '../FacebookSignupLabel';
import classNames from 'classnames';
import { InputTypes } from '../../../constants/form';
import { SubmitHandler } from 'react-hook-form/dist/types/form';

export type SignupStep2ViewProps = {
  //
  submitted: boolean;
  emailRules: RegisterOptions;
  tosRules: RegisterOptions;
  nameRules: RegisterOptions;
  passwordRules: RegisterOptions;
  timezoneRules: RegisterOptions;
  socialAuthTokenRules: RegisterOptions;
  formInstance: UseFormReturn<RegisterWithEmailFormVariables>;
  onSubmit: SubmitHandler<RegisterWithEmailFormVariables>;
  passwordButton: SquareButtonIconsEnum.show | SquareButtonIconsEnum.hide;
  passwordShowToggle: Void;
  passwordFiledType: InputTypes.password | InputTypes.text | InputTypes.hidden;
  signupError: string | JSX.Element;
  signupData?: SocialAuthTokenDataType;
  cancelSocialSignup: Void;
};

export const SignupStep2View = (props: SignupStep2ViewProps): JSX.Element => {
  const passwordClasses = classNames('cell small-12 margin-bottom-small-26', {
    hide: props.passwordFiledType === InputTypes.hidden
  });
  const errorMessage = props.signupError ? (
    <div className={'margin-bottom-small-32'}>
      <BoxNotification type={'error'}>{props.signupError}</BoxNotification>
    </div>
  ) : null;

  const socialProfile = props.signupData ? (
    <div className={'margin-bottom-small-32'}>
      <FacebookSignupLabel
        disabled={props.submitted}
        signupData={props.signupData}
        onClose={() => props.cancelSocialSignup()}
      />
    </div>
  ) : null;
  // we need to cast UseFormReturn<T> back to UseFormReturn
  const formInstance: UseFormReturn = props.formInstance as unknown as UseFormReturn;
  return (
    <div className={classes.Title}>
      <div className={'margin-bottom-small-40'}>
        <h2>Sign Up</h2>
      </div>
      {errorMessage}
      {socialProfile}
      <form className={'grid-x grid-padding-x'} onSubmit={props.formInstance.handleSubmit(props.onSubmit)}>
        <div className={'cell small-12 margin-bottom-small-26'}>
          <Input
            disabled={props.submitted}
            formInstance={formInstance}
            inlinedLabel
            label={'Name'}
            name={'name'}
            rules={props.nameRules}
            size={'large'}
            type={InputTypes.text}
          />
        </div>
        <div className={passwordClasses}>
          <Input
            action={props.passwordShowToggle}
            actionButton={{ icon: props.passwordButton }}
            disabled={props.submitted}
            formInstance={formInstance}
            inlinedLabel
            label={'Password'}
            name={'password'}
            rules={props.passwordRules}
            size={'large'}
            type={props.passwordFiledType}
          />
        </div>
        <Input
          formInstance={formInstance}
          name={'email'}
          readonly={true}
          rules={props.emailRules}
          size={'small'}
          type={InputTypes.hidden}
        />
        <Input
          formInstance={formInstance}
          name={'timezone'}
          readonly={true}
          rules={props.timezoneRules}
          size={'small'}
          type={InputTypes.hidden}
        />
        <Input
          formInstance={formInstance}
          name={'socialAuthToken'}
          readonly={true}
          rules={props.socialAuthTokenRules}
          size={'small'}
          type={InputTypes.hidden}
        />
        <div className={'cell small-12 margin-bottom-small-26'}>
          <Checkbox
            formInstance={formInstance}
            disabled={props.submitted}
            label={[
              { label: { text: 'I agree to' } },
              { link: { text: 'Terms', route: '/terms', openRouteInNewWindow: true } },
              { label: { text: ' & ' } },
              { link: { text: 'Privacy Policy', route: '/privacy', openRouteInNewWindow: true } }
            ]}
            name={'tos'}
            rules={props.tosRules}
            size={'small'}
          />
        </div>
        <div className={'cell small-12'}>
          <Button
            color={ButtonColorsEnum.secondary}
            disabled={props.submitted}
            expanded
            loading={props.submitted}
            size={'large'}
            type={'submit'}
            variant={'solid'}>
            Create Account
          </Button>
        </div>
      </form>
    </div>
  );
};
